import { Tooltip } from 'recharts';

import { cn } from '../../lib/utils';
import { useChartConfig } from './ChartContext';
import type {
  ChartPointValue,
  ChartTooltipContentProps,
  ChartTooltipProps,
} from './types';
import { SYNTHETIC_POINT_SUFFIX } from './utils';

export function ChartTooltipContent({
  active,
  payload,
  label,
  className,
  labelFormatter,
  valueFormatter,
}: ChartTooltipContentProps) {
  const { config } = useChartConfig();

  const filteredPayload = (payload ?? []).filter(
    (item) => item.type !== 'none' && !item.payload?.[SYNTHETIC_POINT_SUFFIX],
  );

  if (!active || filteredPayload.length === 0) {
    return null;
  }

  const formattedLabel = labelFormatter
    ? labelFormatter(label ?? undefined)
    : label == null
      ? ''
      : label instanceof Date
        ? label.toLocaleString()
        : String(label);

  return (
    <div
      data-slot='chart-tooltip'
      className={cn(
        'eye:border-grey-180 eye:rounded-lg eye:border eye:bg-white eye:p-24 eye:text-grey-120',
        className,
      )}
    >
      {formattedLabel ? (
        <div className='eye:text-grey-120 eye:mb-12 eye:text-12 eye:font-semibold eye:uppercase'>
          {formattedLabel}
        </div>
      ) : null}
      <div className='eye:flex eye:flex-col'>
        {filteredPayload.map((item, index) => {
          const configKey = String(item.dataKey ?? item.name ?? index);
          const labelText =
            config[configKey]?.label ?? (item.name as string) ?? '';
          const fallbackValue = item.value as ChartPointValue | undefined;
          const value = valueFormatter
            ? valueFormatter(item.value as ChartPointValue, item)
            : fallbackValue == null
              ? ''
              : String(fallbackValue);
          const shouldShowLabel = Boolean(labelText);
          const swatchColor =
            (item.color as string | undefined) ??
            (item.stroke as string | undefined) ??
            config[configKey]?.color;

          return (
            <div
              key={`${configKey}-${index}`}
              className='eye:rounded-xl eye:flex eye:items-center eye:justify-between eye:gap-12'
            >
              {shouldShowLabel ? (
                <span className='eye:flex eye:items-center eye:gap-8 eye:text-14 eye:font-semibold eye:text-grey-120 eye:wrap-break-word'>
                  {swatchColor ? (
                    <span
                      aria-hidden='true'
                      className='eye:inline-block eye:size-12 eye:shrink-0 eye:rounded-full'
                      style={{ backgroundColor: swatchColor }}
                    />
                  ) : null}
                  {labelText}
                </span>
              ) : null}
              <span className='eye:px-10 eye:body-m eye:shadow-sm eye:rounded-md eye:bg-white eye:font-semibold eye:text-grey-160 eye:text-nowrap'>
                {value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ChartTooltip({
  contentClassName,
  labelFormatter,
  valueFormatter,
  ...props
}: ChartTooltipProps) {
  const cursor = props.cursor ?? { stroke: 'none' };

  return (
    <Tooltip
      {...props}
      cursor={cursor}
      content={
        <ChartTooltipContent
          className={contentClassName}
          labelFormatter={labelFormatter}
          valueFormatter={valueFormatter}
        />
      }
    />
  );
}
